'use client';

import Link from 'next/link';
import { ReactNode } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/src/components/ui/accordion';
import { Button } from '@/src/components/ui/button';
import { IResolvedFaqCardItem } from '@/src/utils/collections/faq/faq-cards.mapper';
import { ILocaleType } from '@/src/types';
import { generateDataLayerFAQSelect } from '@/src/utils/seo/analytics';
import { extractTextFromMarkdoc } from '@/src/utils/collections/entry.mapper';
import { cn } from '@/src/utils/utils';

type IFaqSectionProps = {
  title: ReactNode;
  button: string | null;
  locale: ILocaleType;
  content: IResolvedFaqCardItem[];
};

export const FaqCard = ({
  title,
  button,
  locale,
  content,
}: IFaqSectionProps) => {
  return (
    <div
      className={`mx-auto flex max-w-1200 flex-col gap-8 px-4 lg:flex-row lg:justify-between lg:gap-0 lg:py-18`}
    >
      <div className={'md:space-y-16'}>
        <div className={'max-w-xl text-4xl font-semibold lg:text-6xl'}>
          {title}
        </div>
        <Button size={'md'} className={'hidden lg:inline-block'} asChild>
          <Link
            className={'no-underline active:bg-primary-dark'}
            href={`/${locale.locale}/help`}
            onClick={() => {
              window.dataLayer?.push({
                event: 'click_button',
                click_id: `/${locale.locale}/help`,
                click_label: button,
              });
            }}
          >
            {button}
          </Link>
        </Button>
      </div>
      <div>
        <Accordion
          type="single"
          collapsible
          className="flex w-[500px] max-w-full flex-col gap-4"
        >
          {content.map((item, index) => (
            <AccordionItem
              value={item.question + '_' + index}
              key={item.question + '_' + index}
              onClick={() => {
                window.dataLayer?.push(
                  generateDataLayerFAQSelect(
                    extractTextFromMarkdoc(item.question)
                  )
                );
              }}
              className={cn(
                'rounded-2xl no-underline border-0 transition-colors duration-200 ease-in-out',
                'bg-neutral-100 hover:bg-neutral-200',
                'data-[state=open]:bg-neutral-200'
              )}
            >
              <AccordionTrigger
                className={cn(
                  'rounded-2xl px-8 py-4 text-left text-lg transition-colors',
                  'bg-neutral-100 hover:bg-neutral-200',
                  'data-[state=open]:bg-neutral-200'
                )}
              >
                {item.question}
              </AccordionTrigger>
              <AccordionContent>{item.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
      <Button size={'md'} className={'ml-auto lg:ml-0 lg:hidden'} asChild>
        <Link
          className={'no-underline active:bg-primary-dark'}
          href={`/${locale.locale}/help`}
          onClick={() => {
            window.dataLayer?.push({
              event: 'click_button',
              click_id: `/${locale.locale}/help`,
              click_label: button,
            });
          }}
        >
          {button}
        </Link>
      </Button>
    </div>
  );
};

export default FaqCard;
